// frontend/src/utils/bayHelpers.js

/**
 * Convert bay format from "bay2" to "Bay 2" for display
 * @param {string} bay - The bay in lowercase format (e.g., "bay2")
 * @returns {string} - The bay in display format (e.g., "Bay 2")
 */
export const displayBay = (bay) => {
  if (!bay) return '';
  
  // Handle both formats during transition period
  if (bay.startsWith('Bay ')) return bay;
  
  // Convert "bay2" to "Bay 2"
  const match = bay.match(/bay(\d+)/);
  if (match) {
    return `Bay ${match[1]}`;
  }
  
  return bay;
};

/**
 * Convert bay format from "Bay 2" to "bay2" for API calls
 * @param {string} bay - The bay in any format
 * @returns {string} - The bay in lowercase format (e.g., "bay2")
 */
export const normalizeBay = (bay) => {
  if (!bay) return '';
  
  // Already in correct format
  if (bay.match(/^bay\d+$/)) return bay;
  
  // Convert "Bay 2" to "bay2"
  const match = bay.match(/Bay (\d+)/);
  if (match) {
    return `bay${match[1]}`;
  }
  
  return bay.toLowerCase().replace(/\s+/g, '');
};

/**
 * Get all bay options for dropdowns
 * Can be overridden by location-specific configuration
 * @param {Object} locationConfig - Optional location configuration with bayConfiguration
 * @returns {Array} - Array of bay options with value and label
 */
export const getBayOptions = (locationConfig = null) => {
  // Check for location-specific bay configuration
  if (locationConfig?.bayConfiguration?.bays) {
    return locationConfig.bayConfiguration.bays.map(bay => ({
      value: bay.id,
      label: bay.displayName || bay.name
    }));
  }

  // Default bays - NOTE: Only Bays 1-4 are fully supported in UI
  return [
    { value: 'bay1', label: 'Bay 1' },
    { value: 'bay2', label: 'Bay 2' },
    { value: 'bay3', label: 'Bay 3' },
    { value: 'bay4', label: 'Bay 4' },
    { value: 'bay5', label: 'Bay 5 (UI not configured)' },
    { value: 'bay6', label: 'Bay 6 (UI not configured)' }
  ];
};

/**
 * Check if a bay value is valid
 * @param {string} bay - The bay to validate
 * @param {Object} locationConfig - Optional location configuration with bayConfiguration
 * @returns {boolean} - True if valid bay
 */
export const isValidBay = (bay, locationConfig = null) => {
  const normalized = normalizeBay(bay);

  // Check against location-specific bays if available
  if (locationConfig?.bayConfiguration?.bays) {
    return locationConfig.bayConfiguration.bays.some(b => b.id === normalized);
  }

  // Default validation - accepts bay1 through bay6
  return /^bay[1-6]$/.test(normalized);
};

/**
 * Extract bay from chip ID - supports both old format and numeric IDs
 * @param {string} chipId - The chip ID (e.g., "B1-xxx", "b2", "00001")
 * @param {Object} sensorData - Optional sensor data with bay assignment
 * @returns {string} - The bay ID (e.g., "bay1", "bay2")
 */
export const getBayFromChip = (chipId, sensorData = null) => {
  if (!chipId) return 'bay1';

  // First check if sensor data has explicit bay assignment (for numeric chips)
  if (sensorData?.bay) {
    return sensorData.bay;
  }

  // Check for numeric-only chip IDs (e.g., "00001", "00002")
  if (/^\d+$/.test(chipId)) {
    // Numeric chips require explicit bay assignment in sensor data
    // Return bay1 as default if not assigned
    return sensorData?.bay || 'bay1';
  }

  // Legacy format: Extract bay number from chip ID patterns
  const upperChip = chipId.toUpperCase();

  // Check for "B1-", "B2-", etc. pattern
  const match = upperChip.match(/B(\d+)-/);
  if (match) {
    return `bay${match[1]}`;
  }

  // Check for other legacy patterns
  if (chipId.includes('b2') || chipId.includes('f1')) return 'bay2';
  if (chipId.includes('b3')) return 'bay3';
  if (chipId.includes('b4')) return 'bay4';

  // Default to bay1
  return 'bay1';
};

/**
 * Check if a chip ID is numeric format
 * @param {string} chipId - The chip ID to check
 * @returns {boolean} - True if numeric format
 */
export const isNumericChipId = (chipId) => {
  return /^\d+$/.test(chipId);
};

/**
 * Convert chip ID (STA MAC) to WiFi code (from AP MAC)
 * The ESP32 AP MAC is STA MAC + 1, and WiFi SSID shows last 3 bytes of AP MAC
 * Example: chip ID "e86beafa9ed0" → WiFi code "FA9ED1"
 *
 * @param {string} chipId - The chip ID / STA MAC address (e.g., "e86beafa9ed0")
 * @returns {string} - The WiFi code (e.g., "FA9ED1")
 */
export const chipIdToWifiCode = (chipId) => {
  if (!chipId) return '';

  // Handle numeric chip IDs (legacy format)
  if (isNumericChipId(chipId)) {
    return chipId.padStart(5, '0');
  }

  // For MAC address format (12 hex chars like "e86beafa9ed0")
  // Take the last 6 characters, add 1, and return uppercase
  const cleanId = chipId.toLowerCase().replace(/[^a-f0-9]/g, '');

  if (cleanId.length >= 6) {
    // Get last 6 hex chars (last 3 bytes of STA MAC)
    const lastSix = cleanId.slice(-6);
    // Parse as hex, add 1 (STA→AP), convert back to hex
    const staValue = parseInt(lastSix, 16);
    const apValue = staValue + 1;
    // Return as 6-char uppercase hex (the WiFi code)
    return apValue.toString(16).toUpperCase().padStart(6, '0');
  }

  // Fallback for short/unknown formats
  return chipId.toUpperCase();
};

/**
 * Convert WiFi code back to chip ID suffix
 * Example: WiFi code "FA9ED1" → chip suffix "fa9ed0"
 *
 * @param {string} wifiCode - The WiFi code (e.g., "FA9ED1")
 * @returns {string} - The chip ID suffix (e.g., "fa9ed0")
 */
export const wifiCodeToChipSuffix = (wifiCode) => {
  if (!wifiCode) return '';

  const cleanCode = wifiCode.toLowerCase().replace(/[^a-f0-9]/g, '');

  if (cleanCode.length === 6) {
    // Parse as hex, subtract 1 (AP→STA), convert back to hex
    const apValue = parseInt(cleanCode, 16);
    const staValue = apValue - 1;
    return staValue.toString(16).toLowerCase().padStart(6, '0');
  }

  return wifiCode.toLowerCase();
};

/**
 * Format chip ID for display - shows WiFi code by default
 * @param {string} chipId - The raw chip ID
 * @returns {string} - WiFi code for display (e.g., "FA9ED1")
 */
export const formatChipId = (chipId) => {
  if (!chipId) return '';

  // Convert to WiFi code for user-friendly display
  return chipIdToWifiCode(chipId);
};